import {
  AccessorialCode,
  BillingTerms,
  CapacityProviderType,
  CarrierRef,
  Direction,
  DomainReference,
  EquipmentType,
  IShipment,
  Mode,
  Money,
  ReferenceNumberTypeWellKnownNaturalId,
  TaggedEntity,
  UserRef,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import { CancellationReason } from '../CancellationReason';
import { ResequenceState } from '../consolidation/ResequenceState';
import { FreightAllowance } from '../FreightAllowance';
import { ReferenceNumber } from '../ReferenceNumber';
import { ShippingPoint } from '../ShippingPoint';

import { ShipmentBillTo } from './ShipmentBillTo';
import { ShipmentFinancialSummary } from './ShipmentFinancialSummary';
import { ShipmentHealth } from './ShipmentHealth';
import { ShipmentLineItem } from './ShipmentLineItem';
import { ShipmentStatus } from './ShipmentStatus';
import { ShipmentStop } from './ShipmentStop';
import { ShipmentStopType } from './ShipmentStopType';

type ConstructableShipment = Partial<
  Omit<Shipment, 'clientId' | 'clientName' | 'clientNaturalId'>
>;

export class Shipment implements IShipment, TaggedEntity {
  id?: number;
  owner?: DomainReference;
  status?: ShipmentStatus;
  financialSummary: ShipmentFinancialSummary = new ShipmentFinancialSummary();
  health?: ShipmentHealth;
  carrierId?: number;
  carrierName?: string;
  carrierType?: CapacityProviderType;
  scac?: string;
  mode?: Mode;
  equipmentType?: EquipmentType;
  service?: string;
  quotedMileage?: number;
  estimatedMileage?: number;
  rateShopId?: number;
  acceptedRateQuoteId?: number;
  billTo?: ShipmentBillTo;
  stops: ShipmentStop[] = [];
  lineItems: ShipmentLineItem[] = [];
  referenceNumbers: ReferenceNumber[] = [];
  direction?: Direction;
  billingTerms?: BillingTerms;
  carrierNotes?: string;
  internalNotes?: string;
  requestedAccessorials: AccessorialCode[] = [];
  tags: string[] = [];
  assignedUsers: UserRef[] = [];
  hot = false;
  /** nullable/undefined value is expected - Billing is responsible for initializing this value */
  payable?: boolean;
  swc = false;
  customerRouted = false;
  requireBothArrivalAndDepartureOnStops = false;
  freightAllowance?: FreightAllowance;
  valueOfGoods?: Money;
  cancellationReason?: CancellationReason;
  cancellationComments?: string;
  requestedMode?: Mode;
  source?: string;
  createdBy?: string;
  createdDate?: Date;
  /**
   * @deprecated Use `owner.id` instead
   * @see https://rygen.atlassian.net/browse/CS-7328
   **/
  clientId?: number;
  /**
   * @deprecated Use `owner.name` instead
   * @see https://rygen.atlassian.net/browse/CS-7328
   **/
  clientName?: string;
  /**
   * @deprecated Use `owner.naturalId` instead
   * @see https://rygen.atlassian.net/browse/CS-7328
   **/
  clientNaturalId?: string;

  static colorOptions = [
    '#3D40E1',
    '#CA6899',
    '#D2C72D',
    '#41D78C',
    '#DB9957',
    '#96CA68',
    '#2DD2C7',
    '#5799DB',
    '#85C270',
    '#9966CC',
  ];

  constructor(opts?: ConstructableShipment) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.owner != null) {
      this.owner = new DomainReference(opts.owner);
      this.clientId = this.owner.id;
      this.clientName = this.owner.name;
      this.clientNaturalId = this.owner.naturalId;
    }
    if (opts?.status != null) {
      if (typeof opts?.status === 'string') {
        this.status = ShipmentStatus[opts.status];
      } else {
        this.status = opts.status;
      }
    }
    if (opts?.financialSummary != null) {
      this.financialSummary = new ShipmentFinancialSummary(
        opts.financialSummary,
      );
    }
    if (opts?.health != null) {
      if (typeof opts?.health === 'string') {
        this.health = ShipmentHealth[opts.health];
      } else {
        this.health = opts.health;
      }
    }
    if (opts?.carrierId != null) {
      this.carrierId = opts.carrierId;
    }
    if (opts?.carrierName != null) {
      this.carrierName = opts.carrierName;
    }
    if (opts?.carrierType != null) {
      if (typeof opts?.carrierType === 'string') {
        this.carrierType = CapacityProviderType[opts.carrierType];
      } else {
        this.carrierType = opts.carrierType;
      }
    }
    if (opts?.scac != null) {
      this.scac = opts.scac;
    }
    if (opts?.mode != null) {
      if (typeof opts?.mode === 'string') {
        this.mode = Mode[opts.mode];
      } else {
        this.mode = opts.mode;
      }
    }
    if (opts?.equipmentType != null) {
      this.equipmentType = new EquipmentType(opts.equipmentType);
    }
    if (opts?.service != null) {
      this.service = opts.service;
    }
    if (opts?.quotedMileage != null) {
      this.quotedMileage = opts.quotedMileage;
    }
    if (opts?.estimatedMileage != null) {
      this.estimatedMileage = opts.estimatedMileage;
    }
    if (opts?.rateShopId != null) {
      this.rateShopId = opts.rateShopId;
    }
    if (opts?.acceptedRateQuoteId != null) {
      this.acceptedRateQuoteId = opts.acceptedRateQuoteId;
    }
    if (opts?.billTo != null) {
      this.billTo = new ShipmentBillTo(opts.billTo);
    }

    if (opts?.stops?.length) {
      this.stops = opts.stops.map((val) => new ShipmentStop(val));
    } else {
      this.stops = [Shipment.fallbackOrigin(), Shipment.fallbackDestination()];
    }

    if (opts?.lineItems != null) {
      this.lineItems = opts.lineItems.map((val) => new ShipmentLineItem(val));
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (val) => new ReferenceNumber(val),
      );
    }
    if (opts?.direction != null) {
      this.direction = opts.direction;
    }
    if (opts?.billingTerms != null) {
      this.billingTerms = opts.billingTerms;
    }
    if (opts?.carrierNotes != null) {
      this.carrierNotes = opts.carrierNotes;
    }
    if (opts?.internalNotes != null) {
      this.internalNotes = opts.internalNotes;
    }
    if (opts?.requestedAccessorials != null) {
      this.requestedAccessorials = opts.requestedAccessorials.map(
        (val) => new AccessorialCode(val),
      );
    }
    if (opts?.tags != null) {
      this.tags = opts.tags;
    }
    if (opts?.assignedUsers != null) {
      this.assignedUsers = opts.assignedUsers.map((val) => new UserRef(val));
    }

    if (opts?.hot != null) {
      this.hot = opts.hot;
    }
    if (opts?.payable != null) {
      this.payable = opts.payable;
    }
    if (opts?.swc != null) {
      this.swc = opts.swc;
    }
    if (opts?.customerRouted != null) {
      this.customerRouted = opts.customerRouted;
    }
    if (opts?.requireBothArrivalAndDepartureOnStops != null) {
      this.requireBothArrivalAndDepartureOnStops =
        opts.requireBothArrivalAndDepartureOnStops;
    }
    if (opts?.freightAllowance != null) {
      this.freightAllowance = new FreightAllowance(opts.freightAllowance);
    }
    if (opts?.valueOfGoods) {
      this.valueOfGoods = new Money(opts.valueOfGoods);
    }
    if (opts?.requestedMode != null) {
      this.requestedMode = opts.requestedMode;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.createdBy != null) {
      this.createdBy = opts.createdBy;
    }
    if (opts?.createdDate != null) {
      this.createdDate = opts.createdDate;
    }
  }

  static fallbackOrigin() {
    return new ShipmentStop({
      sequenceNumber: 1,
      stopType: ShipmentStopType.PICKUP,
    });
  }

  static fallbackDestination() {
    return new ShipmentStop({
      sequenceNumber: 2,
      stopType: ShipmentStopType.DROP_OFF,
    });
  }

  get origin(): ShipmentStop {
    return this.stops[0];
  }

  set origin(origin: ShipmentStop) {
    this.stops[0] = origin;
  }

  get destination(): ShipmentStop {
    return this.stops[this.stops.length - 1];
  }

  set destination(destination: ShipmentStop) {
    this.stops[this.stops.length - 1] = destination;
  }

  get isHazmat(): boolean {
    return this.lineItems.some((li) => li.isHazmat);
  }

  get proNumber(): string | undefined {
    const carrierReferenceNumber = this.referenceNumbers.find(
      (refNumber) =>
        refNumber.typeNaturalId ===
        ReferenceNumberTypeWellKnownNaturalId.CARRIER_REFERENCE_NUMBER,
    );

    return carrierReferenceNumber?.value;
  }

  get canceled(): boolean {
    return false;
  }

  get carrier(): CarrierRef {
    return new CarrierRef({
      carrierId: this.carrierId,
      name: this.carrierName,
      scac: this.scac,
      type: this.carrierType,
    });
  }

  getLineItemsForStop(stop: ShippingPoint): ShipmentLineItem[] {
    const isPickup = stop.stopType === ShipmentStopType.PICKUP;
    const seqNumKey = isPickup
      ? 'pickupStopSequenceNumber'
      : 'dropOffStopSequenceNumber';
    return this.lineItems.filter((l) => l[seqNumKey] === stop.sequenceNumber);
  }

  validateStopResequencing(
    oldSequenceNumber: number,
    newSequenceNumber: number,
  ): ResequenceState {
    // The stop we're attempting to move
    const stop = this.stops.find((s) => s.sequenceNumber === oldSequenceNumber);
    if (!stop) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.doesNotExist',
        ),
      };
    }

    const prevStop =
      oldSequenceNumber > 1
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber - 1)
        : undefined;
    const nextStop =
      oldSequenceNumber < this.stops.length
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber + 1)
        : undefined;
    const stopType = stop.stopType;
    if (
      (stopType === ShipmentStopType.PICKUP &&
        newSequenceNumber === this.stops.length) ||
      (stopType === ShipmentStopType.DROP_OFF &&
        oldSequenceNumber === this.stops.length &&
        prevStop?.stopType === ShipmentStopType.PICKUP)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.lastStop',
        ),
      };
    } else if (
      (stopType === ShipmentStopType.DROP_OFF && newSequenceNumber === 1) ||
      (stopType === ShipmentStopType.PICKUP &&
        oldSequenceNumber === 1 &&
        nextStop?.stopType === ShipmentStopType.DROP_OFF)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.firstStop',
        ),
      };
    }

    if (stop.isResolved(this.requireBothArrivalAndDepartureOnStops)) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.alreadyExecuted',
        ),
      };
    }

    // line items being dropped off at this stop
    const lineItemsBeingDroppedOff = this.lineItems.filter(
      (li) => li.dropOffStopSequenceNumber === oldSequenceNumber,
    );
    // the pickup stops for all the line items that are being dropped off at this stop
    const relatedPickupStops = this.stops.filter((stop) =>
      lineItemsBeingDroppedOff.some(
        (li) => li.pickupStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the stops that have already been executed
    const executedStops = this.stops.filter((stop) =>
      stop.isResolved(this.requireBothArrivalAndDepartureOnStops),
    );

    // the highest sequence number of all stops that are pickups for this drop
    // the stop being modified cannot be moved before this sequence number
    const minRelatedSeqNum = relatedPickupStops
      .map((stop) => stop.sequenceNumber)
      .filter(notNull)
      .reduce((a, b) => Math.max(a, b), -1);

    // the highest sequence number of all stops that have been executed
    // the stop being modified cannot be moved before this sequence number
    const minNotExecutedSeqNum = executedStops
      .map((stop) => stop.sequenceNumber)
      .filter(notNull)
      .reduce((a, b) => Math.max(a, b), -1);

    // line items being picked up at this stop
    const lineItemsBeingPickedUp = this.lineItems.filter(
      (li) => li.pickupStopSequenceNumber === oldSequenceNumber,
    );
    // the drop off stops for all the line items that are being picked up at this stop
    const relatedDropoffStops = this.stops.filter((stop) =>
      lineItemsBeingPickedUp.some(
        (li) => li.dropOffStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the lowest sequence number of all stops that are dropoffs for this pickup
    // the stop being modified cannot be moved after this sequence number
    const maxSeqNum = relatedDropoffStops
      .map((stop) => stop.sequenceNumber)
      .filter(notNull)
      .reduce((a, b) => Math.min(a, b), this.stops.length + 1);

    if (newSequenceNumber <= minNotExecutedSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.beforeExecutedStop',
        ),
      };
    } else if (newSequenceNumber <= minRelatedSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.beforeRelatedPickup',
        ),
      };
    } else if (newSequenceNumber >= maxSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.afterRelatedDropoff',
        ),
      };
    } else {
      return {
        canResequence: true,
        canResequenceReason: '',
      };
    }
  }

  resequenceStop(oldSequenceNumber: number, newSequenceNumber: number): void {
    const { canResequence, canResequenceReason } =
      this.validateStopResequencing(oldSequenceNumber, newSequenceNumber);

    if (!canResequence) {
      throw new Error(canResequenceReason);
    }

    const stopOldIndex = this.stops.findIndex(
      (s) => s.sequenceNumber === oldSequenceNumber,
    );
    if (stopOldIndex === -1) {
      throw new Error(
        `Could not find stop with sequenceNumber ${oldSequenceNumber}`,
      );
    }
    const stopNewIndex = newSequenceNumber - 1;

    // Capture which stops are associated with each line item so that pickup and
    // dropoff sequence numbers can be corrected after re-ordering
    const lineItemsToStopsEntries = this.lineItems.map((li) => {
      const pickupStop = this.stops.find(
        (s) => s.sequenceNumber === li.pickupStopSequenceNumber,
      );
      const dropOffStop = this.stops.find(
        (s) => s.sequenceNumber === li.dropOffStopSequenceNumber,
      );
      return [li, [pickupStop, dropOffStop]] as const;
    });
    const lineItemsToStopsMap = new Map(lineItemsToStopsEntries);

    const stops = [...this.stops];
    const [stop] = stops.splice(stopOldIndex, 1);
    stops.splice(stopNewIndex, 0, stop);
    for (let i = 0; i < stops.length; i++) {
      stops[i].sequenceNumber = i + 1;
    }
    this.stops.sort((a, b) => {
      const aSeqNum = a.sequenceNumber ?? -1;
      const bSeqNum = b.sequenceNumber ?? -1;
      return aSeqNum - bSeqNum;
    });

    // Update line items with new sequence numbers
    this.lineItems.forEach((li) => {
      const [pickupStop, dropOffStop] = lineItemsToStopsMap.get(li) ?? [];
      li.pickupStopSequenceNumber = pickupStop?.sequenceNumber;
      li.dropOffStopSequenceNumber = dropOffStop?.sequenceNumber;
    });
  }
}
